import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import BlogList from '../components/blog/BlogList';
import { ScrambleText } from '../components/ui/ScrambleText';
import ghost from '../api/ghost'; // Import the new ghost client
import { MOCK_TAGS } from '../api/ghost-mock'; // Import mock tags directly

// Constants defined outside the component to avoid recreation on each render
const MAX_RETRIES = 3;

function BlogPage() {
  const [isMounted, setIsMounted] = useState(false);
  const [activeTag, setActiveTag] = useState(null);
  const [tags, setTags] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [preloadStatus, setPreloadStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [searchParams, setSearchParams] = useSearchParams();
  const [retryCount, setRetryCount] = useState(0);
  const [debugMode] = useState(() => {
    // Check for debug parameter in URL or localStorage
    return searchParams.get('debug') === 'true' || localStorage.getItem('blogDebug') === 'true';
  });
  
  // Enable debug mode via console
  useEffect(() => {
    if (debugMode) {
      window._blogDebug = {
        clearCache: () => {
          console.log('Blog cache cleared');
          return 'Cache cleared. Refresh the page to see changes.';
        },
        getStatus: () => {
          return {
            isLoading,
            preloadStatus,
            tagsCount: tags.length,
            activeTag,
            error
          };
        }
      };
      console.info('Blog debug mode enabled. Use window._blogDebug.clearCache() to clear cache.');
    }
  }, [debugMode, isLoading, preloadStatus, tags.length, activeTag, error]);

  // Handle tag from URL query param
  useEffect(() => {
    const tagFromQuery = searchParams.get('tag');
    if (tagFromQuery) {
      // Wait for tags to load, then set active tag
      if (tags.length > 0) {
        const foundTag = tags.find(tag => tag.slug === tagFromQuery);
        if (foundTag) {
          setActiveTag(foundTag);
        } else {
          // If tag not found, clear the param
          const newParams = new URLSearchParams(searchParams);
          newParams.delete('tag');
          setSearchParams(newParams);
        }
      }
    }
  }, [searchParams, tags, setSearchParams]);

  // Update URL when tag is changed
  const handleTagChange = useCallback((tag) => {
    setActiveTag(tag);
    
    // Update URL query parameter
    const newParams = new URLSearchParams(searchParams);
    if (tag) {
      newParams.set('tag', tag.slug);
    } else {
      newParams.delete('tag');
    }
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Function to handle retries with a limit
  const handleRetry = useCallback(() => {
    if (retryCount < MAX_RETRIES) {
      setRetryCount(prevCount => prevCount + 1);
      setPreloadStatus('loading');
      setError(null);
      setIsLoading(true);
      console.log(`Retrying... (${retryCount + 1}/${MAX_RETRIES})`);
    } else {
      console.log('Max retries reached, showing fallback content');
      // Show permanent error state after max retries
      setError('Unable to connect to the blog after multiple attempts. Please check your connection and try again later.');
    }
  }, [retryCount]);

  // Load tags data
  useEffect(() => {
    let isActive = true; // Flag to prevent state updates after unmount
    let timeoutId = null;

    const loadTags = async () => {
      if (!isActive) return;
      
      setIsMounted(true);
      setPreloadStatus('loading');
      
      // Add a global timeout for the preload process
      timeoutId = setTimeout(() => {
        if (!isActive) return;
        console.warn('Preload taking too long, marking as error');
        setPreloadStatus('error');
        setError('Loading is taking longer than expected. Content will appear when ready.');
      }, 10000);
      
      try {
        // Fetch tags from Ghost API
        console.log('Fetching tags from Ghost API...');
        let fetchedTags = await ghost.tags.browse({ 
          limit: 'all', 
          include: 'count.posts',
          order: 'count.posts DESC' 
        });
        
        if (!isActive) return;
        clearTimeout(timeoutId);
        
        // Safety check - if tags is not an array, provide an empty array fallback
        if (!Array.isArray(fetchedTags)) {
          console.warn('Tags response is not an array:', fetchedTags);
          fetchedTags = [];
        }
        
        // Filter out tags with no posts and sort by post count
        const filteredTags = fetchedTags
          .filter(tag => tag && tag.count && tag.count.posts > 0)
          .slice(0, 7); // Limit to 7 tags to prevent overflow
        
        setTags(filteredTags);
        setIsLoading(false);
        setPreloadStatus('success');
        setRetryCount(0); // Reset retry count on success
      } catch (err) {
        if (!isActive) return;
        
        console.error('Error loading tags or preloading data:', err);
        setError('Failed to load tags. Please try refreshing the page.');
        setIsLoading(false);
        setPreloadStatus('error');
        setTags([]); // Ensure tags is always an array
        clearTimeout(timeoutId);
      }
    };
    
    loadTags();
    
    // Cleanup function - prevent state updates after component unmounts
    return () => {
      isActive = false;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [retryCount]); // Only retryCount is needed as a dependency

  return (
    <main className="w-full overflow-x-hidden pt-16 bg-black">
      <section className="max-w-7xl mx-auto px-8 sm:px-8 md:px-16 lg:px-24 py-16 sm:py-8 mt-12">
        <div className="mb-10 relative">
          <motion.div
            className="flex flex-wrap items-center gap-4 mb-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <motion.h1
              className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, ease: "easeOut" }}
            >
              <ScrambleText
                text="Blog"
                className="text-[#DBDBDB] font-mono"
                enabled={isMounted}
              />
            </motion.h1>
            
            {/* Preload indicator */}
            {preloadStatus === 'loading' && (
              <motion.div 
                className="ml-3 flex items-center text-[#00CF9D]/70 text-xs"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, delay: 0.3 }}
              >
                <svg className="animate-spin mr-1 h-3 w-3" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Optimizing content...</span>
              </motion.div>
            )}
            
            {debugMode && (
              <div className="text-amber-500 text-xs px-2 py-1 rounded bg-amber-950/30 ml-auto">
                Debug Mode
              </div>
            )}
          </motion.div>
        </div>

        {/* Tag filters */}
        <motion.div 
          className="mb-10 overflow-x-auto -mx-4 px-4 pb-2"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {error && (
            <div className="mb-4 text-[#00CF9D]/80 text-sm p-3 bg-neutral-900 border border-[#00CF9D]/10 rounded-md flex items-center justify-between">
              <div>{error}</div>
              {preloadStatus === 'error' && retryCount < MAX_RETRIES && (
                <button 
                  onClick={handleRetry}
                  className="text-xs bg-[#00CF9D]/10 hover:bg-[#00CF9D]/20 text-[#00CF9D] px-2 py-1 rounded transition-colors"
                >
                  Retry ({retryCount}/{MAX_RETRIES})
                </button>
              )}
            </div>
          )}
          <div className="flex space-x-2 min-w-max">
            {/* "All Posts" filter */}
            <button
              onClick={() => handleTagChange(null)}
              className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                activeTag === null
                  ? 'bg-[#00CF9D] text-black'
                  : 'bg-neutral-900 text-neutral-300 hover:bg-neutral-800'
              }`}
            >
              All Posts
            </button>
            
            {/* Tag filters from Ghost */}
            {isLoading ? (
              <>
                {[...Array(5)].map((_, i) => (
                  <div 
                    key={i}
                    className="whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium bg-neutral-900 animate-pulse h-9 w-20"
                  ></div>
                ))}
              </>
            ) : (
              tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleTagChange(tag)}
                  className={`whitespace-nowrap px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    activeTag && activeTag.id === tag.id
                      ? 'bg-[#00CF9D] text-black'
                      : 'bg-neutral-900 text-neutral-300 hover:bg-neutral-800'
                  }`}
                >
                  {tag.name} {debugMode && <span className="opacity-50">({tag.count?.posts || 0})</span>}
                </button>
              ))
            )}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
        >
          <BlogList tag={activeTag ? activeTag.slug : null} />
        </motion.div>
      </section>
    </main>
  );
}

export default BlogPage; 